import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import PanchTatvaButton from '../components/PanchTatvaButton';
import { useStrings } from '../i18n';
import { useCart } from '../context/CartContext';
import { CartToastContainer, showCartToast } from '../components/CartToast';
import products from '../data/products';

// Element accent colours — matches brand palette
const ELEMENT_COLOURS = {
  earth: { bg: 'bg-[#8B6914]/10', border: 'border-[#8B6914]/30', text: 'text-[#7A5C40]', dot: 'bg-[#8B6914]' },
  water: { bg: 'bg-[#3B82F6]/10', border: 'border-[#3B82F6]/30', text: 'text-[#1d4ed8]', dot: 'bg-[#3B82F6]' },
  fire:  { bg: 'bg-[#EF4444]/10', border: 'border-[#EF4444]/30', text: 'text-[#dc2626]', dot: 'bg-[#EF4444]' },
  air:   { bg: 'bg-[#A3E635]/10', border: 'border-[#84cc16]/30', text: 'text-[#4d7c0f]', dot: 'bg-[#84cc16]' },
  ether: { bg: 'bg-[#A78BFA]/10', border: 'border-[#A78BFA]/30', text: 'text-[#6d28d9]', dot: 'bg-[#A78BFA]' },
};

const ELEMENT_EMOJIS = { earth: '🌿', water: '💧', fire: '🔥', air: '🌬️', ether: '✨' };

export default function QuizResult() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const strings = useStrings();
  const { addToCart } = useCart();
  const t = strings.quiz.result;

  // Feature 11: Redirect if not enabled, or if no result state
  useEffect(() => {
    if (process.env.REACT_APP_ENABLE_AI_QUIZ !== 'true' || !state?.result) {
      navigate('/quiz', { replace: true });
    }
  }, [navigate, state]);

  const { primaryElement, secondaryElement, rationale, products: recommendedProducts } = state.result;

  const colours = ELEMENT_COLOURS[primaryElement] || ELEMENT_COLOURS.earth;
  const emoji = ELEMENT_EMOJIS[primaryElement] || '✨';
  const primaryLabel = t.elements[primaryElement] || primaryElement;
  const secondaryLabel = t.elements[secondaryElement] || secondaryElement;
  const primaryDesc = t.elementDescriptions[primaryElement] || '';

  // Resolve product objects from catalogue (merges image from local data)
  const resolvedProducts = (recommendedProducts || []).map((rp) => {
    const local = products.find((p) => p.id === rp.id || p.element === rp.element);
    return { ...rp, image: local?.image || rp.image };
  });

  const handleAddSet = () => {
    resolvedProducts.forEach((product) => {
      addToCart({ id: product.id, name: product.name, price: product.price, quantity: 1, image: product.image });
    });
    showCartToast(`${resolvedProducts.length} products added to cart!`);
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-12 md:py-20">
      <CartToastContainer />

      {/* Primary Element Hero */}
      <div className={`rounded-3xl border-2 p-8 md:p-12 text-center mb-8 fade-section show ${colours.bg} ${colours.border}`}>
        <div className="text-5xl md:text-6xl mb-4">{emoji}</div>
        <p className="text-sm md:text-base text-bodyText font-sans mb-1">{t.title}</p>
        <h1 className={`text-4xl md:text-5xl font-heading mb-2 ${colours.text}`}>
          {primaryLabel}
        </h1>
        {secondaryElement && secondaryElement !== primaryElement && (
          <p className="text-sm text-textSecondary font-sans mb-3">
            {t.secondary} <span className="capitalize font-semibold">{secondaryLabel}</span>
          </p>
        )}
        <p className={`text-xs font-sans tracking-widest uppercase mb-6 ${colours.text} opacity-70`}>
          {primaryDesc}
        </p>

        {/* Rationale */}
        {rationale && (
          <div className="bg-surface/70 rounded-2xl p-4 md:p-6 text-left">
            <p className="text-xs font-sans text-textSecondary uppercase tracking-widest mb-2">{t.rationale}</p>
            <p className="text-sm md:text-base text-bodyText leading-relaxed font-sans">{rationale}</p>
          </div>
        )}
      </div>

      {/* Recommended Products */}
      {resolvedProducts.length > 0 && (
        <div className="mb-8 fade-section show">
          <h2 className="text-2xl md:text-3xl font-heading text-headingText text-center mb-6">
            {t.recommendedProducts}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-6">
            {resolvedProducts.map((product) => {
              const col = ELEMENT_COLOURS[product.element] || ELEMENT_COLOURS.earth;
              return (
                <Link
                  key={product.id}
                  to={`/element/${product.element}`}
                  className={`bg-surface rounded-2xl shadow-card p-4 md:p-5 flex gap-4 items-center border-2 transition-all hover:-translate-y-1 hover:shadow-xl ${col.border}`}
                >
                  {product.image && (
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-cardBg flex items-center justify-center">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${col.dot}`} />
                      <span className={`text-xs font-sans capitalize ${col.text}`}>{product.element}</span>
                    </div>
                    <p className="text-base font-heading text-headingText truncate">{product.name}</p>
                    <p className="text-sm font-semibold text-secondary mt-0.5">₹{product.price}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Add Set to Cart */}
          <div className="flex justify-center">
            <PanchTatvaButton size="large" onClick={handleAddSet}>
              {t.addSet}
            </PanchTatvaButton>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center fade-section show">
        <PanchTatvaButton variant="outline" onClick={() => navigate('/quiz')}>
          {t.retake}
        </PanchTatvaButton>
        <PanchTatvaButton variant="ghost" onClick={() => navigate('/shop')}>
          {t.shopAll}
        </PanchTatvaButton>
      </div>
    </div>
  );
}
